import React, { forwardRef } from "react";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    Colors,
    PointElement,
    LineElement,
    LineController,
    BarController,
    Filler,
} from "chart.js";
import { Chart } from "react-chartjs-2";

const QuantGraphDetailed = forwardRef(({ variants, initData, kpi, goal, showGoalLine }, ref) => {
    let filteredData = [...initData];

    if (showGoalLine && goal) {
        filteredData = [
            {
                label: "Doel" + (kpi.maximize ? "(min)" : "(max)"),
                data: Array(variants.length).fill(goal.value),
                type: "line",
                borderWidth: 3,
                borderColor: "red",
                legendColor: "red",
                pointStyle: false,
                borderDash: [8, 8],
                order: 1, // Lines appear on top
            },
            ...filteredData.map(dataset => ({
                ...dataset,
                order: dataset.type === 'line' ? dataset.order || 1 : 2, // Preserve line order, set bars to 2
            })),
        ];
    }

    ChartJS.register(
        LinearScale,
        CategoryScale,
        BarElement,
        PointElement,
        LineElement,
        Legend,
        Tooltip,
        LineController,
        BarController,
        Colors,
        Title,
        Filler
    );

    const options = {
        plugins: {
            title: {
                display: true,
                text: kpi.name,
                fullSize: false,
                font: {
                    size: 14,
                },
            },
            legend: {
                display: true,
                position: initData.length > 8 ? 'right' : 'bottom',
                align: "start",
                labels: {
                    generateLabels: function(chart) {
                        const datasets = chart.data.datasets;
                        return datasets.map((dataset, i) => {
                            const isLine = dataset.type === 'line';
                            const color = dataset.legendColor || (Array.isArray(dataset.backgroundColor) ? dataset.backgroundColor[0] : dataset.backgroundColor);
                            
                            return {
                                text: dataset.label,
                                fillStyle: isLine ? 'transparent' : color,
                                strokeStyle: color,
                                lineWidth: isLine ? 3 : 0,
                                lineDash: isLine && dataset.borderDash ? dataset.borderDash : [],
                                hidden: !chart.isDatasetVisible(i),
                                index: i,
                            };
                        });
                    }
                }
            },
            filler: {
                propagate: true,
            },
        },
        responsive: true,
        scales: {
            x: {
                stacked: true,
            },
            y: {
                stacked: true,
                title: {
                    display: true,
                    text: kpi.unit,
                },
                afterDataLimits: function(scale) {
                    scale.max = scale.max * 1.01
                },
            },
        },
    };

    const data = {
        labels: variants,
        datasets: filteredData,
    };
    if (filteredData === null) {
        return null;
    }

    // Determine chart type based on data
    const hasMixedTypes = filteredData.some(dataset => dataset.type === 'line') && filteredData.some(dataset => dataset.type === 'bar');
    const chartType = hasMixedTypes ? 'bar' : 'bar'; // Chart.js handles mixed types with 'bar' as base type
    
    return <Chart ref={ref} type={chartType} options={options} data={data} redraw />;
});

QuantGraphDetailed.displayName = 'QuantGraphDetailed';

export default QuantGraphDetailed;
